import { useMemo, useState } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus, Mail, Pencil, Users, MailOpen } from 'lucide-react'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { cn } from '@/shared/lib/utils'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { EditMemberDialog } from '@/admin/pages/settings/edit-member-dialog'
import { MemberProfileDialog } from '@/admin/pages/settings/member-profile-dialog'
import { StatCards } from '@/admin/components/shared/stat-cards'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { SearchInput } from '@/shared/components/ui/search-input'
import {
  DataTable,
  ColumnToggle,
  FilterBar,
  type DataTableColumn,
  type SortingState,
  type ActiveFilter,
} from '@/admin/components/data-table'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'


import { toastApiError } from '@/shared/lib/api-error'
import { useFormatters } from '@/shared/lib/formatting'

interface MemberRole {
  uuid: string
  name: string
  is_admin: boolean
  is_primary: boolean
}

interface Member {
  uuid: string
  user_email: string
  user_name: string
  user_first_name: string
  user_last_name: string
  role_uuid: string
  role_name: string
  roles: MemberRole[]
  is_owner: boolean
}

interface RoleOption {
  uuid: string
  name: string
  is_admin: boolean
}

interface Invitation {
  uuid: string
  email: string
  role: string
  first_name: string
  last_name: string
  expiry: string
  created_at: string
  accepted: boolean
  expired: boolean
}

interface MemberStatCards {
  total: number
  owners: number
  roles: number
}

interface MembersResponse {
  results?: Member[]
  count?: number
  stat_cards?: MemberStatCards
}

interface InvitationsResponse {
  results?: Invitation[]
  count?: number
}

const MEMBERS_PAGE_SIZE = 25
const INVITATIONS_PAGE_SIZE = 10

export function UsersTab() {
  const { orgUuid } = useOrg()
  const { t } = useTranslation()
  const { formatDate } = useFormatters()
  const queryClient = useQueryClient()

  // Members table state (server-side pagination / search / sort / filter)
  const [memberSearch, setMemberSearch] = useState('')
  const [memberPage, setMemberPage] = useState(1)
  const [memberPageSize, setMemberPageSize] = useState(MEMBERS_PAGE_SIZE)
  const [memberSorting, setMemberSorting] = useState<SortingState | null>(null)
  const [memberFilters, setMemberFilters] = useState<ActiveFilter[]>([])
  const [memberColumnVisibility, setMemberColumnVisibility] = useState<
    Record<string, boolean>
  >({})
  const [memberColumnOrder, setMemberColumnOrder] = useState<string[]>([])

  // Pending invitations table state
  const [invitationSearch, setInvitationSearch] = useState('')
  const [invitationPage, setInvitationPage] = useState(1)
  const [invitationSorting, setInvitationSorting] =
    useState<SortingState | null>(null)

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [cancellingInvite, setCancellingInvite] = useState<Invitation | null>(
    null,
  )
  const [editingMember, setEditingMember] = useState<Member | null>(null)
  const [viewingMember, setViewingMember] = useState<Member | null>(null)

  // ─── Reference data ────────────────────────────────────────
  // Full role list (page_size=1000): the invite/edit dropdown and the role
  // filter must offer every role, not just the first paginated page.
  const rolesQuery = useQuery<RoleOption[]>({
    queryKey: ['organisations', orgUuid, 'roles', 'all'],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/roles/`, { params: { page_size: 1000 } })
        .then((r) => r.data.results ?? r.data),
    enabled: !!orgUuid,
  })
  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data])

  // ─── Members ───────────────────────────────────────────────
  const memberParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: memberPage,
      page_size: memberPageSize,
    }
    if (memberSearch) params.search = memberSearch
    if (memberSorting) {
      params.ordering = `${memberSorting.direction === 'desc' ? '-' : ''}${memberSorting.field}`
    }
    for (const f of memberFilters) params[f.field] = f.value
    return params
  }, [memberPage, memberPageSize, memberSearch, memberSorting, memberFilters])

  const membersQuery = useQuery<MembersResponse>({
    queryKey: ['organisations', orgUuid, 'members', memberParams],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/members/`, { params: memberParams })
        .then((r) => r.data),
    enabled: !!orgUuid,
    // Keep the current page painted while the next page/filter loads, so rows
    // and stat cards do not flash empty on navigation.
    placeholderData: keepPreviousData,
  })
  const members = membersQuery.data?.results ?? []
  const memberCount = membersQuery.data?.count ?? 0
  const statCards = membersQuery.data?.stat_cards ?? null

  // ─── Pending invitations ───────────────────────────────────
  const invitationParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: invitationPage,
      page_size: INVITATIONS_PAGE_SIZE,
    }
    if (invitationSearch) params.search = invitationSearch
    if (invitationSorting) {
      params.ordering = `${invitationSorting.direction === 'desc' ? '-' : ''}${invitationSorting.field}`
    }
    return params
  }, [invitationPage, invitationSearch, invitationSorting])

  const invitationsQuery = useQuery<InvitationsResponse>({
    queryKey: ['organisations', orgUuid, 'invitations', invitationParams],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/invitations/`, {
          params: invitationParams,
        })
        .then((r) => r.data),
    enabled: !!orgUuid,
    placeholderData: keepPreviousData,
  })
  const invitations = invitationsQuery.data?.results ?? []
  const invitationCount = invitationsQuery.data?.count ?? 0

  // ─── Mutations ─────────────────────────────────────────────
  const inviteMutation = useMutation({
    mutationFn: (payload: Record<string, string | undefined>) =>
      api.post(`/organisations/${orgUuid}/members/invite/`, payload),
    onSuccess: () => {
      toast.success(t('users.invitationSent'))
      setDialogOpen(false)
      setInvitationPage(1)
      queryClient.invalidateQueries({
        queryKey: ['organisations', orgUuid, 'members'],
      })
      queryClient.invalidateQueries({
        queryKey: ['organisations', orgUuid, 'invitations'],
      })
    },
    onError: (err) => toastApiError(err, t('users.errorSendInvitation')),
  })

  const resendMutation = useMutation({
    mutationFn: (invitationUuid: string) =>
      api.post(`/organisations/${orgUuid}/invitations/${invitationUuid}/resend/`),
    onSuccess: () => {
      toast.success(t('users.invitationResent'))
      queryClient.invalidateQueries({
        queryKey: ['organisations', orgUuid, 'invitations'],
      })
    },
    onError: (err) => toastApiError(err, t('users.errorResendInvitation')),
  })

  const cancelMutation = useMutation({
    mutationFn: (invitationUuid: string) =>
      api.delete(`/organisations/${orgUuid}/invitations/${invitationUuid}/`),
    onSuccess: () => {
      toast.success(t('users.invitationCancelled'))
      setCancellingInvite(null)
      queryClient.invalidateQueries({
        queryKey: ['organisations', orgUuid, 'invitations'],
      })
    },
    onError: (err) => toastApiError(err, t('users.errorCancelInvitation')),
  })

  // Stable references (React Query guarantees `.mutate` identity) so the
  // memoised columns never capture a stale closure.
  const resendInvitation = resendMutation.mutate

  const inviteFields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'first_name',
        testId: 'users-firstname-input',
        label: t('users.firstName'),
        placeholder: t('users.firstName'),
        value: '',
        autoFocus: true,
        required: t('users.firstNameRequired'),
      },
      {
        type: 'text',
        name: 'last_name',
        testId: 'users-lastname-input',
        label: t('users.lastName'),
        placeholder: t('users.lastName'),
        value: '',
      },
      {
        type: 'email',
        name: 'email',
        testId: 'users-email-input',
        label: t('common.email'),
        placeholder: t('users.emailPlaceholder'),
        value: '',
        className: 'col-span-2',
        required: t('users.emailRequired'),
      },
      {
        type: 'select',
        name: 'role_uuid',
        testId: 'users-role-input',
        label: t('common.role'),
        placeholder: t('users.selectRole'),
        value: '',
        className: 'col-span-2',
        options: roles.map((role) => ({
          value: role.uuid,
          label: role.is_admin
            ? `${role.name} ${t('users.adminRoleSuffix')}`
            : role.name,
        })),
      },
    ],
    [roles, t],
  )
  const { Form: InviteForm, ref: inviteFormRef } = useForm(
    {
      name: 'users-invite-form',
      submit: 'footer',
      fields: inviteFields,
    },
    async (rawValues) => {
      if (!orgUuid) return
      const values = rawValues as Record<string, string>
      await inviteMutation.mutateAsync({
        email: String(values.email ?? '').trim(),
        first_name: String(values.first_name ?? '').trim(),
        last_name: String(values.last_name ?? '').trim(),
        role_uuid: String(values.role_uuid ?? '') || undefined,
      })
    },
    [dialogOpen, inviteFields, roles, orgUuid],
  )

  const memberColumns = useMemo<DataTableColumn<Member>[]>(
    () => [
      {
        id: 'name',
        header: t('common.name'),
        sortable: true,
        sortField: 'user__first_name',
        width: '26%',
        cell: (row) => (
          <span className="text-[13px] text-[var(--ink)]">
            {row.user_name || '-'}
          </span>
        ),
      },
      {
        id: 'email',
        header: t('common.email'),
        sortable: true,
        sortField: 'user__email',
        width: '30%',
        cell: (row) => (
          <span className="block truncate text-[12px] text-[var(--ink-2)]">
            {row.user_email}
          </span>
        ),
      },
      {
        id: 'role',
        header: t('common.role'),
        sortable: true,
        sortField: 'role__name',
        meta: {
          filterType: 'select',
          filterField: 'role',
          filterOptions: roles.map((r) => ({ value: r.uuid, label: r.name })),
        },
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1.5">
            {(row.roles.length > 0
              ? row.roles
              : row.role_name
                ? [
                    {
                      uuid: row.role_uuid,
                      name: row.role_name,
                      is_admin: false,
                    },
                  ]
                : []
            ).map((role) => (
              <Badge
                key={role.uuid}
                variant="secondary"
                className="inline-flex rounded-full bg-[var(--bg-subtle)] px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-2)]"
              >
                {role.name}
              </Badge>
            ))}
            {row.is_owner && (
              <Badge
                variant="secondary"
                className="inline-flex rounded-full bg-[var(--green-bg)] px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-[var(--green)]"
              >
                {t('users.owner')}
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        width: '64px',
        cell: (row) => (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="icon-sm"
              data-testid={`users-edit-${row.uuid}`}
              aria-label={t('users.editMember', 'Edit user')}
              onClick={(e) => {
                e.stopPropagation()
                setEditingMember(row)
              }}
              className="cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        ),
      },
    ],
    [t, roles],
  )

  const invitationColumns = useMemo<DataTableColumn<Invitation>[]>(
    () => [
      {
        id: 'invitee',
        header: t('users.invitee'),
        sortable: true,
        sortField: 'email',
        width: '32%',
        cell: (row) => (
          <div>
            <p className="text-[13px] text-[var(--ink)]">{row.email}</p>
            {(row.first_name || row.last_name) && (
              <p className="text-[11px] text-[var(--ink-3)]">
                {row.first_name} {row.last_name}
              </p>
            )}
          </div>
        ),
      },
      {
        id: 'role',
        header: t('common.role'),
        width: '22%',
        cell: (row) => (
          <span className="text-[13px] text-[var(--ink-2)]">{row.role}</span>
        ),
      },
      {
        id: 'status',
        header: t('users.status'),
        width: '14%',
        cell: (row) => (
          <Badge
            variant="secondary"
            className={cn(
              'inline-flex w-fit rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.1em]',
              row.expired
                ? 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
                : 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
            )}
          >
            {row.expired ? t('users.statusExpired') : t('users.statusPending')}
          </Badge>
        ),
      },
      {
        id: 'sent',
        header: t('users.sent'),
        sortable: true,
        sortField: 'created_at',
        width: '14%',
        cell: (row) => (
          <span className="text-[11px] text-[var(--ink-3)]">
            {formatDate(row.created_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        width: '140px',
        cell: (row) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.stopPropagation()
                resendInvitation(row.uuid)
              }}
              className="cursor-pointer text-[10px] uppercase tracking-[0.1em] text-[var(--green)]"
            >
              {t('users.resend')}
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.stopPropagation()
                setCancellingInvite(row)
              }}
              className="cursor-pointer text-[10px] uppercase tracking-[0.1em] text-[var(--status-error)]"
            >
              {t('common.cancel')}
            </Button>
          </div>
        ),
      },
    ],
    [t, formatDate, resendInvitation],
  )

  const showInvitationsSection =
    invitationCount > 0 ||
    invitationSearch.length > 0 ||
    invitationsQuery.isPending

  if (!orgUuid) {
    return (
      <p className="clara-small py-8 text-center">
        {t('users.noOrgSelected')}
      </p>
    )
  }

  return (
    <div>
      {/* Stat cards */}
      <StatCards
        cards={[
          { key: 'total', label: t('users.statTotal'), value: statCards?.total ?? 0 },
          { key: 'owners', label: t('users.statOwners'), value: statCards?.owners ?? 0 },
          { key: 'roles', label: t('users.statRoles'), value: statCards?.roles ?? 0 },
        ]}
      />
      {/* Section header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="clara-section-label">{t('users.title')}</h3>
        </div>
        <Button
          data-testid="users-add-btn"
          onClick={() => setDialogOpen(true)}
          className="px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] transition-all duration-150 cursor-pointer flex items-center gap-1.5 bg-[var(--green)] text-[var(--bg)] hover:bg-[var(--green-hover)]"
        >
          <Plus className="h-3 w-3" />
          {t('users.invite')}
        </Button>
      </div>
      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] pb-6">
        <div className="w-full md:max-w-xs">
          <SearchInput
            data-testid="users-search"
            placeholder={t('users.searchPlaceholder')}
            value={memberSearch}
            onChange={(v) => {
              setMemberSearch(v)
              setMemberPage(1)
            }}
            className="text-[13px]"
          />
        </div>
        <div className="flex-1" />
        <FilterBar
          columns={memberColumns}
          activeFilters={memberFilters}
          onFiltersChange={(f) => {
            setMemberFilters(f)
            setMemberPage(1)
          }}
        />
        <ColumnToggle
          columns={memberColumns}
          visibility={memberColumnVisibility}
          onVisibilityChange={setMemberColumnVisibility}
          columnOrder={memberColumnOrder}
          onColumnOrderChange={setMemberColumnOrder}
        />
      </div>
      {/* Members table */}
      <DataTable<Member>
        tableId="org-members"
        columns={memberColumns}
        data={members}
        totalCount={memberCount}
        isLoading={membersQuery.isPending}
        isFetching={membersQuery.isFetching}
        page={memberPage}
        pageSize={memberPageSize}
        onPageChange={setMemberPage}
        onPageSizeChange={(size) => {
          setMemberPageSize(size)
          setMemberPage(1)
        }}
        sorting={memberSorting}
        onSortingChange={(s) => {
          setMemberSorting(s)
          setMemberPage(1)
        }}
        columnVisibility={memberColumnVisibility}
        columnOrder={memberColumnOrder}
        highlightQuery={memberSearch}
        onRowClick={(row) => setViewingMember(row)}
        rowTestId={(row) => `users-item-${row.uuid}`}
        emptyStateNode={
          <EmptyState
            icon={Users}
            title={t('users.emptyState')}
            description={t('users.emptyStateDescription')}
          />
        }
      />
      {/* Pending invitations */}
      {showInvitationsSection && (
        <section className="mt-10">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <h3 className="clara-section-label">{t('users.pendingInvitations')}</h3>
            <div className="flex-1" />
            <div className="w-full md:max-w-xs">
              <SearchInput
                data-testid="invitations-search"
                placeholder={t('users.searchInvitationsPlaceholder')}
                value={invitationSearch}
                onChange={(v) => {
                  setInvitationSearch(v)
                  setInvitationPage(1)
                }}
                className="text-[13px]"
              />
            </div>
          </div>
          <DataTable<Invitation>
            tableId="org-invitations"
            columns={invitationColumns}
            data={invitations}
            totalCount={invitationCount}
            isLoading={invitationsQuery.isPending}
            isFetching={invitationsQuery.isFetching}
            page={invitationPage}
            pageSize={INVITATIONS_PAGE_SIZE}
            onPageChange={setInvitationPage}
            onPageSizeChange={() => {}}
            sorting={invitationSorting}
            onSortingChange={(s) => {
              setInvitationSorting(s)
              setInvitationPage(1)
            }}
            highlightQuery={invitationSearch}
            emptyStateNode={
              <EmptyState
                icon={MailOpen}
                title={t('users.noInvitationsTitle')}
                description={t('users.noInvitationsDescription')}
              />
            }
          />
        </section>
      )}
      {/* Invite dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) {
            inviteFormRef.current?.resetForm({
              values: {
                first_name: '',
                last_name: '',
                email: '',
                role_uuid: '',
              },
            })
          }
        }}
      >
        <DialogContent className="w-full max-w-md">
          <DialogHeader className="flex items-center justify-between mb-5">
            <div>
              <DialogTitle className="text-[15px] text-[var(--ink)]">{t('users.inviteTitle')}</DialogTitle>
              <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
                {t('users.inviteDescription')}
              </p>
            </div>
          </DialogHeader>

          <InviteForm className="grid grid-cols-2 gap-3" />
            <DialogFooter className="flex justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-2">
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  inviteFormRef.current?.resetForm()
                  setDialogOpen(false)
                }}
                className="cursor-pointer border-none bg-transparent px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                form="users-invite-form"
                disabled={inviteMutation.isPending}
                data-testid="users-dialog-submit"
                className="flex cursor-pointer items-center gap-1.5 rounded-[4px] border-none bg-[var(--green)] px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 disabled:opacity-50"
              >
                {inviteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                {t('users.sendInvitation')}
              </Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={cancellingInvite !== null}
        onOpenChange={(open) => {
          if (!open) setCancellingInvite(null)
        }}
        title={t('users.cancelInvitationTitle')}
        description={
          cancellingInvite
            ? t('users.cancelInvitationDescription', { email: cancellingInvite.email })
            : undefined
        }
        confirmLabel={t('users.cancelInvitationConfirm')}
        isPending={cancelMutation.isPending}
        onConfirm={() => {
          if (cancellingInvite) cancelMutation.mutate(cancellingInvite.uuid)
        }}
      />

      <EditMemberDialog
        orgUuid={orgUuid}
        member={editingMember}
        roleOptions={roles}
        open={editingMember !== null}
        onOpenChange={(open) => {
          if (!open) setEditingMember(null)
        }}
        onSaved={() => {
          setEditingMember(null)
          queryClient.invalidateQueries({
            queryKey: ['organisations', orgUuid, 'members'],
          })
        }}
      />

      <MemberProfileDialog
        membershipUuid={viewingMember?.uuid ?? null}
        fallbackName={viewingMember?.user_name}
        fallbackEmail={viewingMember?.user_email}
        open={viewingMember !== null}
        onOpenChange={(open) => {
          if (!open) setViewingMember(null)
        }}
      />
    </div>
  );
}
